import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useScreen, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { plural, pluralWithCount } from '../../lib/plural';
import { Notice } from '../../ui/parts';
import { useCreateTenant, useTenants } from '../api';
import type { TenantOverview } from '../../api/types';

/** Вкладка «Организации»: все центры платформы. */
export function Tenants() {
  const navigate = useNavigate();
  const data = useTenants();

  useScreen('Организации', null);

  const list = data.data?.tenants ?? [];
  const platform = data.data?.platformTenant;

  return (
    <Screen>
      {/* Заголовка секции здесь нет: он повторял бы слово в слово и шапку экрана,
          и вкладку внизу. Остаётся разбивка по статусам — она говорит то, чего
          ещё не сказали. */}
      {list.length ? <p className="lead">{statusSummary(list)}</p> : null}

      {data.isPending ? <p className="lead">Загружаем…</p> : null}

      {/* Кнопка стоит над списком: организаций на платформе десятки, и добираться
          до неё скроллом через весь список пришлось бы каждый раз. Так же стоит
          «Добавить услугу» на экране услуг. */}
      <button className="add" onClick={() => navigate('/owner/new')}>＋ Добавить</button>

      <div className="rows">
        {list.map((t) => (
          <button className="rw" key={t.id} onClick={() => navigate(`/owner/${t.id}`)}>
            <span className="rw-body">
              <b>{t.title}{t.id === platform ? ' · платформа' : ''}</b>
              {subtitleLines(t).map((line) => <span key={line}>{line}</span>)}
              <span>Статус: <strong>{statusLabel(t)}</strong></span>
            </span>
          </button>
        ))}
      </div>

      <Notice kind="calm">
        Пока у организации нет собственного бота, администратор входит через бот платформы. После
        подключения собственного бота все ссылки будут вести в него.
      </Notice>
    </Screen>
  );
}

/** Подпись карточки — по строке на факт: в узкой колонке одна длинная строка рвётся как попало. */
function subtitleLines(t: TenantOverview): string[] {
  return [
    t.bot.connected ? `@${t.bot.username}` : 'бот не подключён',
    t.admins.length ? `Админ: ${t.admins[0].name}` : 'Админ не назначен',
    [
      pluralWithCount(t.counts.admins, 'админ', 'админа', 'админов'),
      pluralWithCount(t.counts.specialists, 'специалист', 'специалиста', 'специалистов'),
      pluralWithCount(t.counts.children, 'клиент', 'клиента', 'клиентов'),
    ].join(' · '),
  ];
}

function statusLabel(t: TenantOverview): string {
  if (t.status === 'suspended') return 'Приостановлена';
  if (t.status !== 'active') return 'Закрыта';
  return 'Активна';
}

/**
 * Разбивка по статусам вместо общего числа.
 *
 * «5 организаций» отвечает на вопрос, которого никто не задаёт: список и так
 * перед глазами, а посчитать в нём строки можно самому. Знать надо другое —
 * сколько из них работает, а сколько приостановлено: приостановленная
 * организация не принимает записи, и это то, за чем сюда заходят.
 *
 * Нулевые группы не пишем: «0 приостановлено» — шум, а не сведения.
 */
function statusSummary(list: TenantOverview[]): string {
  const active = list.filter((t) => t.status === 'active').length;
  const suspended = list.filter((t) => t.status === 'suspended').length;
  const closed = list.length - active - suspended;

  // Прилагательное краткое: «1 активна», «2 активны», «5 активны» — форма для
  // 2–4 и для 5+ совпадает, поэтому в plural они одинаковые.
  const groups: [count: number, one: string, many: string][] = [
    [active, 'активна', 'активны'],
    [suspended, 'приостановлена', 'приостановлены'],
    [closed, 'закрыта', 'закрыты'],
  ];

  return groups
    .filter(([n]) => n > 0)
    .map(([n, one, many]) => `${n} ${plural(n, one, many, many)}`)
    .join(', ');
}

/** Подэкран «Новая организация»: заводим и сразу выпускаем ссылку администратору. */
export function TenantNew() {
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreateTenant();

  const [title, setTitle] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminUsername, setAdminUsername] = useState('');
  const [tz, setTz] = useState('Europe/Moscow');

  const ready = title.trim().length > 2;

  useScreen('Новая организация', null);

  return (
    <Screen>
      <p className="lead">
        Создайте организацию — после этого мы сформируем ссылку для её администратора. Подключать
        собственного бота на этом этапе не нужно.
      </p>

      <div className="field">
        <label htmlFor="pnTitle">Название организации</label>
        <input id="pnTitle" type="text" placeholder="Студия «Радуга»" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="pnAdmin">Имя администратора</label>
        <input id="pnAdmin" type="text" placeholder="Мария" value={adminName} onChange={(e) => setAdminName(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="pnTg">Telegram администратора</label>
        <input id="pnTg" type="text" placeholder="@maria" value={adminUsername} onChange={(e) => setAdminUsername(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="pnTz">Часовой пояс</label>
        <select id="pnTz" value={tz} onChange={(e) => setTz(e.target.value)}>
          <option value="Europe/Kaliningrad">Калининград · UTC+2</option>
          <option value="Europe/Moscow">Москва · UTC+3</option>
          <option value="Asia/Yekaterinburg">Екатеринбург · UTC+5</option>
          <option value="Asia/Novosibirsk">Новосибирск · UTC+7</option>
          <option value="Asia/Vladivostok">Владивосток · UTC+10</option>
        </select>
      </div>


      <button
        className="btn solid"
        disabled={!ready || create.isPending}
        onClick={() =>
          create.mutate(
            {
              title: title.trim(),
              tz,
              adminName: adminName.trim() || undefined,
              adminUsername: adminUsername.trim() || undefined,
            },
            {
              onSuccess: (res) => navigate(`/owner/${res.tenant.id}`, { replace: true }),
              onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось создать организацию'),
            },
          )
        }
      >
        {create.isPending ? 'Создаём…' : 'Создать и получить ссылку'}
      </button>
    </Screen>
  );
}
